import { cleanDecode } from "../../../src/utils/CommonUtils";
import { itx } from "../../../src/translationItx/itx";
import { comparePdfLabels, pdfHeaderText } from "../../../src/pdf/pdfTagName";
import { loadPdfDocumentHeader, type PdfDocumentHeader } from "../../../src/pdf/PdfDocumentHeader";
import { protectPdfOutput } from "../../../src/pdf/protectPdfOutput";
import { jsPDF } from "jspdf";
import "svg2pdf.js";
import { specialChannelTypes } from "../../../src/models/ChannelType";
import { TAG_TEMPERATUR } from "../../../src/models/Constants";
import { loadDeviceFrontImage } from "../../../src/pdf/loadDeviceFrontImage";
import type Button from "../../../src/models/Button";
import { loadPdfDocumentLogo } from "../../../src/pdf/pdfDocumentLogo";
import type { C1_PdfChapterArtifact } from "../lib/C1_PdfChapter";
import { registerC1PdfFonts } from "../lib/C1_PdfFonts";

const margin = 45;
const contentTop = 94;
const footerTopOffset = 48;
const lineColor = "#aeb9bf";
const mutedColor = "#586a75";



/** Renders the same front image used by the main application, including generic labels. */
async function drawButtonImage(document: jsPDF, button: Button, pageWidth: number, y: number): Promise<number> {
  const source = await loadDeviceFrontImage(button);
  if (!source) return 0;
  const params = button.baseProps?.buttonBaseProps ?? button.template?.genericParams?.buttonBaseProps;
  let width = positiveDimension(params?.widthMm);
  let height = positiveDimension(params?.heightMm);
  let svg: SVGSVGElement | undefined;
  let image: HTMLImageElement | undefined;
  if (source.startsWith("data:image/svg+xml")) {
    const comma = source.indexOf(",");
    const markup = source.slice(0, comma).includes(";base64")
      ? atob(source.slice(comma + 1)) : decodeURIComponent(source.slice(comma + 1));
    svg = new DOMParser().parseFromString(markup, "image/svg+xml").documentElement as unknown as SVGSVGElement;
  } else {
    image = await loadPdfDocumentLogo(source);
    if (!image) return 0;
    width = positiveDimension(image.naturalWidth);
    height = positiveDimension(image.naturalHeight);
  }
  const scale = Math.min((pageWidth * 0.30) / width, 300 / height);
  width *= scale;
  height *= scale;
  const x = margin;
  if (svg) await document.svg(svg, { x, y, width, height });
  else if (image) document.addImage(image, x, y, width, height);
  return height;
}

function positiveDimension(value: number | undefined): number {
  return value !== undefined && Number.isFinite(value) && value > 0 ? value : 90;
}

/** Supplies the button location to the shared document header. */
function drawHeader(document: jsPDF, floorLabel: string, roomLabel: string, header: PdfDocumentHeader) {
  header.draw(document, { planType: itx("c1.ui.Taster_Liste", "Taster-Liste"), hierarchy: [floorLabel, roomLabel] });
}

function drawFooter(document: jsPDF, page: number, totalPages: number, projectName: string) {
  const pageWidth = document.internal.pageSize.getWidth();
  const pageHeight = document.internal.pageSize.getHeight();
  const y = pageHeight - footerTopOffset;
  document.setDrawColor(lineColor);
  document.line(margin, y, pageWidth - margin, y);
  document.setTextColor(mutedColor);
  document.setFont("helvetica", "normal");
  document.setFontSize(6.5);
  document.text(itx("c1.ui.Projekt_value1", "Projekt: {{value1}}", { "{{value1}}": String(projectName) }), margin, y + 14);
  document.text(itx("c1.ui.Stand_value1", "Stand: {{value1}}", { "{{value1}}": String(new Intl.DateTimeFormat(state.settings.uiLanguage).format(new Date())) }), pageWidth / 2, y + 14, {
    align: "center",
  });
  document.text(`${page} / ${totalPages}`, pageWidth - margin, y + 14, { align: "right" });
}

export function buttonListSpecification(button: Button): Array<[string, string]> {
  const generic = button.template?.genericParams?.buttonBaseProps;
  const params = generic;
  const info = button.template?.knxProductData.productInfo;
  const order = button.template?.orderInformation;
  const rows: Array<[string, string]> = [
    [itx("c1.ui.Tastertyp", "Tastertyp"), button.template?.name || "\u2013"],
    ["Hersteller", cleanDecode(info?.ManufacturerName || "") || "\u2013"],
    ["EAN-Nummer", order?.eanNumber ? String(order.eanNumber) : "\u2013"],
    ["Bestellnummer", order?.articleNo || info?.OrderNumber || "\u2013"],
  ];
  if (button.template?.genericProductType === "Button" && params) {
    rows.push(
      [itx("c1.ui.Wippen", "Wippen"), String(generic.numberOfButtonRows)],
      [itx("print.orientatin", "Ausrichtung"), params.horizontal ? itx("PanelLayoutMain.horizontal", "Horizontal") : itx("PanelLayoutMain.vertikal", "Vertikal")],
      [itx("variantUtility.Color", "Farbe"), params.color || "\u2013"],
      [itx("c1.ui.Display", "Display"), params.hasDisplay ? itx("ProjectList.yes", "Ja") : itx("ProjectList.no", "Nein")],
      [itx("channel.patchButton", "Patch"), ["ohne", "einfach", "mit langem Dr\u00fccken"][params.supportsSlap]],
    );
  }
  return rows;
}

export async function createC1ButtonListPdf(projectName = ""): Promise<C1_PdfChapterArtifact> {
  const document = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4", compress: true });
  await registerC1PdfFonts(document);
  document.setProperties({ title: `ESTADA ${itx("c1.ui.Tastenliste", "Tastenliste")}`, author: "ESTADA" });
  const header = await loadPdfDocumentHeader();
  const language = state.settings.uiLanguage;
  const buttons = Array.from(new Map(state.allButtons()
    .filter(button => !button.isSensor && !button.isDecentralActor)
    .map(button => [button.id, button])).values());
  buttons.sort((a, b) => comparePdfLabels(a.room?.parentFloor, b.room?.parentFloor, language)
    || comparePdfLabels(a.room, b.room, language) || comparePdfLabels(a, b, language));
  const roomCounts = new Map<string | undefined, number>();
  const entries = buttons.map(button => {
    const room = button.room;
    const floor = room?.parentFloor;
    const roomButtonIndex = roomCounts.get(room?.id) ?? 0;
    roomCounts.set(room?.id, roomButtonIndex + 1);
    return { button, roomButtonIndex, floorLabel: pdfHeaderText(floor), roomLabel: pdfHeaderText(room), floor };
  });

  if (!entries.length) {
    document.setFont("helvetica", "normal");
    document.setFontSize(11);
    document.text(itx("c1.ui.Im_Projekt_sind_keine_Taster_vorhanden", "Im Projekt sind keine Taster vorhanden."), margin, contentTop);
    return { blob: await protectPdfOutput(document.output("blob"), "list"), pages: [{ title: itx("c1.ui.Keine_Taster", "Keine Taster"), pageIndex: 0 }] };
  }

  const pageWidth = document.internal.pageSize.getWidth();
  const bottom = document.internal.pageSize.getHeight() - footerTopOffset - 20;
  const pages: C1_PdfChapterArtifact["pages"] = [];
  const lineHeight = 12;
  for (const entry of entries) {
    const startPage = (continuation = false) => {
      if (pages.length) document.addPage("a4", "portrait");
      pages.push({ title: `${entry.roomLabel}${continuation || entry.roomButtonIndex > 0 ? " \u00b7 Fortsetzung" : ""}`,
        sectionTitle: entry.floor?.name, pageIndex: pages.length });
      drawHeader(document, entry.floorLabel, entry.roomLabel, header);
      document.setTextColor("#000000");
      document.setFont("helvetica", "bold");
      document.setFontSize(15);
      const title = document.splitTextToSize(pdfHeaderText(entry.button) || itx("menubar.buttons", "Taster"), pageWidth - margin * 2);
      document.text(title, margin, contentTop);
      return contentTop + title.length * 18 + 15;
    };
    const previewY = startPage();
    const previewHeight = await drawButtonImage(document, entry.button, pageWidth, previewY);
    const specX = margin + pageWidth * 0.30 + 24;
    const specWidth = pageWidth - margin - specX;
    let specY = previewY + 9 + 5 * 72 / 25.4;
    document.setTextColor("#172b3a");
    document.setFont("helvetica", "bold");
    document.setFontSize(12);
    document.text("Spezifikation", specX, specY);
    specY += 22;
    for (const [label, value] of buttonListSpecification(entry.button)) {
      document.setFont("helvetica", "bold");
      document.setFontSize(8.5);
      document.text(label, specX, specY);
      document.setFont("helvetica", "normal");
      const lines = document.splitTextToSize(value, specWidth - 90);
      document.text(lines, specX + 90, specY);
      specY += Math.max(19, lines.length * lineHeight + 5);
    }
    let y = Math.max(previewY + previewHeight, specY) + 28;
    const columns = [margin, margin + 105, margin + 270];
    const widths = [95, 155, pageWidth - margin - columns[2]];
    const heading = () => {
      document.setTextColor("#172b3a");
      document.setFont("helvetica", "bold");
      document.setFontSize(12);
      document.text("Tastenbelegung", margin, y);
      y += 22;
      document.setFillColor("#ecf1f3");
      document.rect(margin, y - 12, pageWidth - margin * 2, 20, "F");
      document.setFontSize(8.5);
      ["Taste / Funktion", "Beschriftung", "Zugeordnete Verbraucher"].forEach((label, index) => document.text(label, columns[index], y));
      y += 8;
      document.setDrawColor(lineColor);
      document.line(margin, y, pageWidth - margin, y);
      y += 15;
    };
    if (y + 55 > bottom) y = startPage(true);
    heading();
    const channels = entry.button.buttonChannels.asArray().filter(channel => {
      if (specialChannelTypes.includes(channel.channelType)) return false;
      return [channel.tag, channel.mappedChannel?.tag].every(tag => !tag?.toUpperCase().startsWith("TX")
        || tag.toUpperCase() === TAG_TEMPERATUR || tag.toUpperCase() === "TX__T");
    }).sort((a, b) => a.tag.localeCompare(b.tag, language, { numeric: true }));
    if (!channels.length) {
      document.setFont("helvetica", "normal");
      document.setFontSize(9);
      document.text("Keine Tastenbelegung vorhanden.", margin, y);
    }
    for (const channel of channels) {
      const partners = new Map<string, string>();
      for (const connection of channel.connections.asArray()) {
        const partner = connection.getPartner(channel);
        if (partner) partners.set(partner.id, pdfHeaderText(partner));
      }
      const values = [
        `${channel.mappedChannel?.name || channel.tag}\n${itx("channelType." + channel.channelType, channel.channelType)}`,
        channel.nameOrConnectedNames || "\u2013",
        Array.from(partners.values()).join("\n") || "\u2013",
      ];
      document.setFont("helvetica", "normal");
      document.setFontSize(9);
      const lines: string[][] = values.map((value, index) => document.splitTextToSize(value, widths[index]));
      let offset = 0;
      const length = Math.max(...lines.map(column => column.length));
      while (offset < length) {
        if (y + lineHeight > bottom) { y = startPage(true); heading(); }
        document.setFont("helvetica", "normal");
        document.setFontSize(9);
        const count = Math.min(length - offset, Math.floor((bottom - y) / lineHeight));
        lines.forEach((column, index) => {
          const part = column.slice(offset, offset + count);
          if (part.length) document.text(part, columns[index], y);
        });
        offset += count;
        y += count * lineHeight;
      }
      document.setDrawColor(lineColor);
      document.line(margin, y, pageWidth - margin, y);
      y += 16;
    }
  }
  for (let index = 0; index < pages.length; index++) {
    document.setPage(index + 1);
    drawFooter(document, index + 1, pages.length, projectName);
  }
  return { blob: await protectPdfOutput(document.output("blob"), "list"), pages };
}
